'use client'

import { useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

// Capture gate (Task 4 of docs/superpowers/plans/2026-07-14-pricing-calculator.md).
// Replaces T3's CompletionSlot placeholder once the wizard is fully
// answered. Copy (headline/body) comes straight from the Calculator
// global's resultsHeadline/captureCopy — no hardcoded pitch copy lives
// here, only field labels and UI chrome.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const fieldClass =
  'w-full rounded-xl border border-white/10 bg-neutral-950/60 px-4 py-3 text-base text-white placeholder-white/30 outline-none focus:border-kingdom-gold/60'

export function CaptureForm({ config, answers, onSuccess }) {
  const shouldReduceMotion = useReducedMotion()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [company, setCompany] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    const trimmedName = name.trim()
    const trimmedEmail = email.trim().toLowerCase()
    if (!trimmedName) {
      setError('Your name is required.')
      return
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError('Enter a valid email address.')
      return
    }

    setBusy(true)
    try {
      const res = await fetch('/api/calculator/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers,
          contact: {
            name: trimmedName,
            email: trimmedEmail,
            ...(phone.trim() ? { phone: phone.trim() } : {}),
            ...(company.trim() ? { company: company.trim() } : {}),
          },
          honeypot,
        }),
      })

      if (res.status === 404) {
        // T5 (the submit route) isn't live yet in this task's scope. In
        // NON-production builds only, a verification probe can inject a
        // canned success response on window.__CALC_FIXTURE__
        // ({ ok, resultsToken, snapshot }) to exercise the BreakdownStack
        // reveal end-to-end without the real route — the env gate is a
        // compile-time constant, so the fixture read is dead-code-eliminated
        // from production bundles entirely. Production 404s always fall
        // through to the graceful "almost ready" state below.
        // T5 replaces this whole branch once /api/calculator/submit ships.
        const fixture =
          process.env.NODE_ENV !== 'production' && typeof window !== 'undefined'
            ? window.__CALC_FIXTURE__
            : null
        if (fixture && fixture.ok && fixture.snapshot) {
          onSuccess({ resultsToken: fixture.resultsToken || null, snapshot: fixture.snapshot })
          return
        }
        setError(
          "We couldn't submit just now — your answers are still here; please try again in a moment.",
        )
        return
      }

      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        setError(data.error || 'Something went wrong — try again.')
        return
      }
      onSuccess({ resultsToken: data.resultsToken || null, snapshot: data.snapshot })
    } catch {
      setError('Network hiccup — try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="calc-capture">
      <p className="text-xs font-semibold tracking-[0.2em] text-kingdom-gold uppercase">All set</p>
      <h2 className="mt-3 font-display text-xl font-medium text-white sm:text-2xl">
        {config?.resultsHeadline || 'Your revenue engine build'}
      </h2>
      {config?.captureCopy && (
        <p className="mt-3 text-sm leading-relaxed text-white/60">{config.captureCopy}</p>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-3" noValidate>
        {/* Honeypot — matches the careers/apply idiom (src/components/careers/ApplyForm.jsx):
            visually hidden, unfocusable, never seen or filled by a real
            visitor. A bot that autofills every field trips it; the submit
            route (T5) answers silent-ok when it's non-empty. */}
        <div className="sr-only" aria-hidden="true">
          <label htmlFor="calc-website">Leave this field blank</label>
          <input
            id="calc-website"
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-neutral-400" htmlFor="calc-name">
            Your name
          </label>
          <input
            id="calc-name"
            type="text"
            autoComplete="name"
            required
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${fieldClass} mt-1.5`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-400" htmlFor="calc-email">
            Email address
          </label>
          <input
            id="calc-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${fieldClass} mt-1.5`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-400" htmlFor="calc-phone">
            Phone <span className="text-neutral-600">(optional)</span>
          </label>
          <input
            id="calc-phone"
            type="tel"
            autoComplete="tel"
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={`${fieldClass} mt-1.5`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-400" htmlFor="calc-company">
            Company <span className="text-neutral-600">(optional)</span>
          </label>
          <input
            id="calc-company"
            type="text"
            autoComplete="organization"
            placeholder="Company name"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className={`${fieldClass} mt-1.5`}
          />
        </div>

        {error && (
          <motion.p
            role="alert"
            data-testid="calc-capture-error"
            initial={shouldReduceMotion ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
            className="text-sm text-red-400"
          >
            {error}
          </motion.p>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="calc-capture-submit"
          className="min-h-11 w-full rounded-full bg-kingdom-gold px-6 py-3 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-60"
        >
          {busy ? 'Unlocking…' : 'See my itemized breakdown'}
        </button>
      </form>
    </div>
  )
}
